import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { useSwap } from '../contexts/SwapContext';
import { useAuth } from '../contexts/AuthContext';
import { FadeInPage } from '../components/common/PageTransition';
import { DonkatsuLoading } from '../components/common/LoadingStates';
import toast from 'react-hot-toast';

const SwapPage: React.FC = () => {
  const { myOffers, allOffers, isLoading, addOffer, cancelOffer, addRequest, acceptRequest, rejectRequest } = useSwap();
  const { user } = useAuth();
  
  // 내 제안이 있으면 'my' 탭을 기본값으로 설정
  const [activeTab, setActiveTab] = useState<'market' | 'my'>(myOffers.length > 0 ? 'my' : 'market');
  
  // 제안 생성 모달
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [giveDate, setGiveDate] = useState('');
  const [wantDate, setWantDate] = useState('');
  const [reason, setReason] = useState('');

  // myOffers 변경 시 탭 자동 전환
  useEffect(() => {
    if (myOffers.length > 0 && activeTab === 'market') {
      setActiveTab('my');
    }
  }, [myOffers.length]);

  const handleCreateOffer = async () => {
    if (!giveDate || !wantDate || !reason) {
      toast.error('모든 필드를 입력해주세요.');
      return;
    }

    try {
      await addOffer(new Date(giveDate), new Date(wantDate), reason);
      setIsCreateModalOpen(false);
      setGiveDate('');
      setWantDate('');
      setReason('');
    } catch (error) {
      // 에러는 Context에서 처리
    }
  };

  const handleCancelOffer = async (offerId: string) => {
    if (window.confirm('정말 이 제안을 취소하시겠습니까?')) {
      try {
        await cancelOffer(offerId);
      } catch (error) {
        // 에러는 Context에서 처리
      }
    }
  };

  const handleRequest = async (offerId: string) => {
    const giveDate = prompt('내가 줄 날짜 (YYYY-MM-DD):');
    if (!giveDate) return;

    const contact = prompt('연락처 (선택):') || undefined;

    try {
      await addRequest(offerId, new Date(giveDate), contact);
    } catch (error) {
      // 에러는 Context에서 처리
    }
  };

  const handleAcceptRequest = async (requestId: string) => {
    if (window.confirm('이 교환 요청을 승인하시겠습니까?')) {
      try {
        await acceptRequest(requestId);
      } catch (error) {
        // 에러는 Context에서 처리
      }
    }
  };

  const handleRejectRequest = async (requestId: string) => {
    if (window.confirm('이 교환 요청을 거절하시겠습니까?')) {
      try {
        await rejectRequest(requestId);
      } catch (error) {
        // 에러는 Context에서 처리
      }
    }
  };

  if (isLoading) {
    return <DonkatsuLoading />;
  }

  return (
    <FadeInPage>
      <main className="container mx-auto px-4 py-8 pb-0">
        <div className="max-w-4xl mx-auto">
          {/* 헤더 */}
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-primary mb-2">
              🤝 스왑 마켓
            </h1>
            <p className="text-base md:text-lg text-gray-600">
              안전하게 근무일을 교환하세요!
            </p>
          </div>

          {/* 탭 + 제안 버튼 */}
          <div className="flex items-center gap-2 mb-6">
            <button
              onClick={() => setActiveTab('market')}
              className={`px-3 py-2 rounded-lg font-medium text-sm transition-all ${
                activeTab === 'market'
                  ? 'bg-gradient-to-r from-purple-400 via-pink-400 to-rose-400 text-white shadow-lg scale-105'
                  : 'bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700'
              }`}
            >
              🛒 마켓 ({allOffers.length})
            </button>
            <button
              onClick={() => setActiveTab('my')}
              className={`px-3 py-2 rounded-lg font-medium text-sm transition-all ${
                activeTab === 'my'
                  ? 'bg-gradient-to-r from-blue-400 via-cyan-400 to-teal-400 text-white shadow-lg scale-105'
                  : 'bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-700'
              }`}
            >
              📝 내 제안 ({myOffers.length})
            </button>
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="px-3 py-2 rounded-lg font-medium text-sm bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-400 text-white shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all whitespace-nowrap"
            >
              ➕ 새 제안
            </button>
          </div>

          {/* 마켓 탭 */}
          {activeTab === 'market' && (
            <div className="space-y-4">
              {allOffers.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-xl shadow-lg">
                  <p className="text-6xl mb-4">🛒</p>
                  <p className="text-lg text-gray-500">현재 진행 중인 제안이 없습니다</p>
                </div>
              ) : (
                allOffers.map((offer) => {
                  const alreadyRequested = offer.requests.length > 0;
                  const isMyOffer = offer.userId === user?.id;
                  
                  return (
                    <div
                      key={offer.id}
                      className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-bold text-primary mb-1">
                            {offer.user.name} {isMyOffer && '(나)'}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {format(offer.createdAt, 'M월 d일 HH:mm', { locale: ko })}
                          </p>
                        </div>
                        <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                          🟢 진행 중
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="bg-red-50 rounded-lg p-4">
                          <p className="text-xs text-gray-600 mb-1">내가 줄 날짜</p>
                          <p className="text-lg font-bold text-red-600">
                            {format(offer.giveDate, 'M월 d일 (EEE)', { locale: ko })}
                          </p>
                        </div>
                        <div className="bg-blue-50 rounded-lg p-4">
                          <p className="text-xs text-gray-600 mb-1">내가 받을 날짜</p>
                          <p className="text-lg font-bold text-blue-600">
                            {format(offer.wantDate, 'M월 d일 (EEE)', { locale: ko })}
                          </p>
                        </div>
                      </div>

                      <div className="mb-4">
                        <p className="text-sm text-gray-600 mb-1">사유</p>
                        <p className="text-base">{offer.reason}</p>
                      </div>

                      {!isMyOffer && (
                        <button
                          onClick={() => handleRequest(offer.id)}
                          disabled={alreadyRequested}
                          className={`w-full py-3 rounded-lg font-bold transition-all ${
                            alreadyRequested
                              ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                              : 'bg-gradient-to-r from-orange-400 to-orange-500 text-white hover:shadow-lg hover:scale-105 active:scale-95'
                          }`}
                        >
                          {alreadyRequested ? '✅ 요청 완료' : '🚀 교환 요청하기'}
                        </button>
                      )}
                      
                      {isMyOffer && (
                        <div className="text-center py-3 bg-blue-50 rounded-lg text-blue-700 font-medium">
                          💡 내가 등록한 제안입니다
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* 내 제안 탭 */}
          {activeTab === 'my' && (
            <div className="space-y-4">
              {myOffers.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-xl shadow-lg">
                  <p className="text-6xl mb-4">📝</p>
                  <p className="text-lg text-gray-500 mb-2">등록한 제안이 없습니다</p>
                  <p className="text-sm text-gray-400">교환할 날짜를 등록해보세요!</p>
                </div>
              ) : (
                myOffers.map((offer) => {
                  const pendingRequests = offer.requests.filter(r => r.status === 'PENDING');
                  
                  return (
                    <div
                      key={offer.id}
                      className="bg-white rounded-xl shadow-lg p-6"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-bold text-primary mb-1">
                            내 제안
                          </h3>
                          <p className="text-sm text-gray-500">
                            {format(offer.createdAt, 'M월 d일 HH:mm', { locale: ko })}
                          </p>
                        </div>
                        <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                          offer.status === 'OPEN' ? 'bg-green-100 text-green-800' :
                          offer.status === 'COMPLETED' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {offer.status === 'OPEN' ? '🟢 진행 중' :
                           offer.status === 'COMPLETED' ? '✅ 완료' : '🚫 취소'}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="bg-red-50 rounded-lg p-4">
                          <p className="text-xs text-gray-600 mb-1">내가 줄 날짜</p>
                          <p className="text-base font-bold text-red-600">
                            {format(offer.giveDate, 'M월 d일 (EEE)', { locale: ko })}
                          </p>
                        </div>
                        <div className="bg-blue-50 rounded-lg p-4">
                          <p className="text-xs text-gray-600 mb-1">내가 받을 날짜</p>
                          <p className="text-base font-bold text-blue-600">
                            {format(offer.wantDate, 'M월 d일 (EEE)', { locale: ko })}
                          </p>
                        </div>
                      </div>

                      <div className="mb-4">
                        <p className="text-sm text-gray-600 mb-1">사유</p>
                        <p className="text-base">{offer.reason}</p>
                      </div>

                      {/* 요청 목록 */}
                      {pendingRequests.length > 0 && (
                        <div className="bg-gray-50 rounded-lg p-4 mb-4">
                          <p className="text-sm font-semibold text-gray-700 mb-3">
                            📨 교환 요청 ({pendingRequests.length}건)
                          </p>
                          <div className="space-y-2">
                            {pendingRequests.map((request) => (
                              <div
                                key={request.id}
                                className="bg-white rounded-lg p-3 flex items-center justify-between"
                              >
                                <div className="flex-1">
                                  <p className="font-medium text-gray-800">
                                    {request.user.name}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    줄 날짜: {format(request.giveDate, 'M월 d일', { locale: ko })}
                                  </p>
                                  {request.contact && (
                                    <p className="text-xs text-gray-500">
                                      연락처: {request.contact}
                                    </p>
                                  )}
                                </div>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleAcceptRequest(request.id)}
                                    className="px-3 py-1 bg-green-500 text-white rounded-lg text-sm font-medium hover:bg-green-600 transition-all"
                                  >
                                    승인
                                  </button>
                                  <button
                                    onClick={() => handleRejectRequest(request.id)}
                                    className="px-3 py-1 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition-all"
                                  >
                                    거절
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* 취소 버튼 */}
                      {offer.status === 'OPEN' && (
                        <button
                          onClick={() => handleCancelOffer(offer.id)}
                          className="w-full py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-all"
                        >
                          ❌ 제안 취소
                        </button>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>

        {/* 제안 생성 모달 */}
        {isCreateModalOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full">
              <h2 className="text-2xl font-bold text-primary mb-4">
                🤝 새 교환 제안
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    내가 줄 날짜 💗
                  </label>
                  <input
                    type="date"
                    value={giveDate}
                    onChange={(e) => setGiveDate(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    내가 받을 날짜 💎
                  </label>
                  <input
                    type="date"
                    value={wantDate}
                    onChange={(e) => setWantDate(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    사유
                  </label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="교환하고 싶은 이유를 입력해주세요"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent resize-none"
                    rows={3}
                  />
                </div>
              </div>

              <div className="flex gap-2 mt-6">
                <button
                  onClick={() => setIsCreateModalOpen(false)}
                  className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-all"
                >
                  취소
                </button>
                <button
                  onClick={handleCreateOffer}
                  className="flex-1 py-3 bg-gradient-to-r from-orange-400 to-orange-500 text-white rounded-lg font-bold hover:shadow-lg transition-all"
                >
                  등록
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </FadeInPage>
  );
};

export default SwapPage;
